/** Owner/admin-only workspace setting: allow uncensored/adult local models.
 * Hidden for everyone else (the server also refuses a non-admin write, and
 * refuses enabling for a Learning/minor workspace, but an owner/admin can
 * always turn a stored setting off). Default OFF. */
import { useEffect, useId, useRef, useState } from 'react';

import { fetchLocalAiAdultWorkspace, setLocalAiAdultWorkspace, type LocalAiAdultWorkspace } from '../../api/localAi';

export const ADULT_WORKSPACE_COPY = 'Allow uncensored/adult models in this workspace. '
  + 'You confirm you are 18+ and accept responsibility for this content.';

export function AdultWorkspaceToggle({ onChanged }: { onChanged?: () => void }) {
  const [state, setState] = useState<LocalAiAdultWorkspace | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const active = useRef(true);
  const id = useId();
  useEffect(() => {
    active.current = true;
    fetchLocalAiAdultWorkspace().then((next) => { if (active.current) setState(next); }).catch(() => {});
    return () => { active.current = false; };
  }, []);
  if (!state || !state.can_manage) return null;
  const change = async (enabled: boolean) => {
    setBusy(true); setError('');
    try {
      const next = await setLocalAiAdultWorkspace(enabled);
      if (active.current) setState({ ...state, enabled: next.enabled, updated_at: next.updated_at,
        ...(next.can_enable === undefined ? {} : { can_enable: next.can_enable }) });
      onChanged?.();
    } catch {
      if (active.current) setError('The adult content setting could not be changed. Confirm you accepted the account terms (18+) and try again.');
    } finally {
      if (active.current) setBusy(false);
    }
  };
  // Eligibility gates only turning it ON; an owner can always turn it off.
  const blockedOn = state.can_enable === false && !state.enabled;
  return <section className="settings-subsection" aria-label="Adult content">
    <label htmlFor={id}>
      <input id={id} type="checkbox" checked={state.enabled} disabled={busy || blockedOn}
        onChange={(event) => void change(event.currentTarget.checked)} />
      {' '}{ADULT_WORKSPACE_COPY}
    </label>
    {error ? <p className="error" role="alert">{error}</p> : null}
  </section>;
}
